<template>
  <div>
    <template v-if="!isDetail">
      <template v-if="announcementList.length > 0">
        <ListHeader
          v-if="showAll"
          tableId="listTable"
          :filteredList="filteredAnnouncements"
          :fullList="announcementList"
          :search="search"
          :fields="announcementFields"
          :pagenate="pagenate"
          textPlaceholder="タイトルと内容を検索"
          @updateSearch="updateSearch"
          @changePage="changePage"
        >
          <div v-if="isAuth">
            <app-button class="default" @click="openEditForm(0)">{{ addButtonText }}</app-button>
          </div>
        </ListHeader>
        <app-button v-if="!showAll && isAuth" class="default" @click="openEditForm(0)" style="margin-bottom: 10px">
          {{ addButtonText }}
        </app-button>

        <template v-if="filteredAnnouncements.length > 0">
          <!-- テーブル -->
          <b-table
            id="listTable"
            small
            stacked="sm"
            striped
            :items="filteredAnnouncements"
            :fields="tableFields"
            :per-page="pagenate.perPage"
            :sordBy="pagenate.sortBy"
            responsive
            :no-local-sorting="true"
            :sortDesc="pagenate.sortDesc"
            :current-page="pagenate.currentPage"
            @sort-changed="changeSort"
          >
            <template v-slot:cell(title)="data">
              <SHIcons
                v-if="getTitleClass(data.item.status) === 'replyed'"
                name="reply"
                height="12"
                iconColor="#999999"
              />
              <a
                :class="getTitleClass(data.item.status)"
                href=""
                @click.prevent.stop="handleClickDetail(data.item.announcement_id)"
              >
                {{ data.item.title }}
              </a>
              <span class="draft" v-if="data.item.publishing_status !== '0'">
                ({{ $constList.announcementStateList[data.item.publishing_status] }})
              </span>
            </template>
            <template v-slot:cell(status)="data">
              {{ $constList.replyStateList[data.item.status] }}
              <span v-if="data.item.status === 2" class="text-muted small">
                <br />
                {{ arrangedDate(data.item.updated_at) }}
              </span>
            </template>
            <template v-slot:cell(started_at)="data">
              {{ arrangedDate(data.item.started_at) }}
              <template v-if="data.item.updated_at !== data.item.created_at">
                <br />
                <span class="text-muted small">{{ arrangedDate(data.item.updated_at) }}更新</span>
              </template>
            </template>
            <template v-slot:cell(publishing_status)="data">
              {{ $constList.announcementStateList[data.item.publishing_status] }}
            </template>
            <template v-slot:cell(ended_at)="data">
              <template v-if="data.item.ended_at">
                {{ arrangedDate(data.item.ended_at) }}
              </template>
              <template v-else>無期限</template>
            </template>
            <template v-slot:cell(management)="data">
              <b-nav>
                <b-nav-item @click="openEditForm(data.item.announcement_id)">
                  <SHIcons class="edit-announcement" name="draft" iconColor="#68c3d4" />
                </b-nav-item>
                <b-nav-item @click.prevent.stop="deleteAnnouncement(data.item.announcement_id, data.item.title)">
                  <SHIcons class="delete-announcement" name="trash" iconColor="#68c3d4" />
                </b-nav-item>
              </b-nav>
            </template>
          </b-table>
        </template>

        <!-- 表示できる要素がない場合 -->
        <template v-else>
          <div class="bg-info text-white">登録されたお知らせはありません。</div>
        </template>

        <slot><!--もっと見る はここに入る--></slot>
      </template>

      <!-- お知らせ未登録の場合 -->
      <template v-else>
        <app-button v-if="isAuth" class="default" style="margin-bottom: 10px" @click="openEditForm(0)">
          {{ addButtonText }}
        </app-button>
        <div class="bg-info text-white">お知らせは登録されていません。</div>
      </template>
    </template>

    <!-- 詳細画面 -->
    <template v-else>
      <AnnouncementDetail
        v-if="!isAuth"
        :announcement="selectedAnnouncement"
        :index="detailIndex"
        :total="filteredAnnouncements.length"
        @showOther="showOther"
        @showList="showList"
        @click-profile="handleClickProfile($event)"
        @click-reply="handleClickReply($event)"
        @click-favorite="handleClickFavorite($event)"
        @read="handleRead($event)"
      />
      <AnnouncementDetailForAdmin
        v-else
        @click-delete="deleteAnnouncementFromDetail()"
        @showOther="showOther"
        @showList="showList"
        @click-profile="handleClickProfile($event)"
        @click-edit="openEditForm($event)"
        :announcement="selectedAnnouncement"
        :index="detailIndex"
        :total="filteredAnnouncements.length"
        @createNewAnnouncement="createNewAnnouncementFromReply"
      />
    </template>

    <b-modal id="announcement-modal" :no-close-on-backdrop="true" :title="modalTitle" :hide-footer="true" size="xl">
      <ErrorMessage
        v-if="announcementInfoForEdit.announcement_id !== 0"
        :status="infoApiStatus.status"
        :customMessage="infoApiStatus.customMessage"
      />
      <AnnouncementEdit
        v-if="infoApiStatus.status === 200 || announcementInfoForEdit.announcement_id === 0"
        :announcement="announcementInfoForEdit"
        @update="update"
        @closeModal="closeEditForm($event)"
      ></AnnouncementEdit>
    </b-modal>

    <ProfileModal id="profile-modal" modalTitle="お知らせに返信した修了生情報" :clickedUrlString="profileUrlString" />
  </div>
</template>

<script>
import AnnouncementDetail from '@/components/Announcement/AnnouncementDetail'
import AnnouncementDetailForAdmin from '@/components/AnnouncementDetailForAdmin.vue'

import ListHeader from '@/components/ListHeader.vue'
import ProfileModal from '@/components/ProfileModal.vue'
import AnnouncementEdit from '@/components/AnnouncementEdit.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import SHIcons from '@/components/ico/SHIcons'
import AppButton from '@/components/presentationals/AppButton'
import { mapState, mapGetters } from 'vuex'

import api from '@/api'
import dayjs from 'dayjs'

import listJs from '@/transformer/list'
import baseJs from '@/transformer/base'
import { toDisplayAnnouncement, getEmptyAnnouncementInfo, getArrangedAnnouncement } from '@/transformer/announcement'

export default {
  name: 'AnnouncementList',
  components: {
    AnnouncementDetail,
    AnnouncementDetailForAdmin,
    AnnouncementEdit,
    ListHeader,
    ProfileModal,
    ErrorMessage,
    SHIcons,
    AppButton
  },

  props: {
    showAll: Boolean,
    announcementFields: Array
  },

  computed: {
    isAuth() {
      return baseJs.isAuth()
    },
    ...mapState({
      announcement: (state) => state.Announcement.announcement,
      infoApiStatus: (state) => state.Announcement.infoApiStatus,
      listReaction: (state) => state.Announcement.listReaction
    }),
    ...mapGetters('Announcement/', ['announcementList']),
    // 検索欄の内容で成果を絞り込み、ソート順に並べ替え
    filteredAnnouncements() {
      const list = this.showAll ? this.announcementList : this.announcementList.slice(0, 5)
      return listJs.getFilteredArray(list, this.announcementFields, this.search, this.pagenate)
    },
    tableFields() {
      return this.announcementFields.filter((e) => !e.hidden)
    },
    selectedAnnouncement() {
      return toDisplayAnnouncement(this.filteredAnnouncements[this.detailIndex])
    }
  },

  data() {
    return {
      search: listJs.getEmptySearch(this.announcementFields),
      addButtonText: 'お知らせ登録',
      detailIndex: 0,
      isDetail: false,
      pagenate: listJs.getDefaultPagenate(),
      announcementInfoForEdit: getEmptyAnnouncementInfo(),
      modalTitle: 'お知らせ登録',
      profileUrlString: ''
    }
  },

  methods: {
    arrangedDate(date) {
      return dayjs(date).format('YYYY年MM月DD日')
    },
    changeSort(e) {
      this.pagenate.sortBy = e.sortBy
      this.pagenate.sortDesc = e.sortDesc
    },
    changePage(e) {
      this.pagenate = e
    },
    createNewAnnouncementFromReply(userIds) {
      this.modalTitle = this.addButtonText
      this.$nextTick().then(() => {
        this.openEditForm(0, userIds)
      })
    },
    // detailIndexをもとにデータを削除
    deleteAnnouncementFromDetail() {
      this.deleteAnnouncement(this.announcement.announcement_id, this.announcement.title)
    },
    deleteAnnouncement(announcementId, title) {
      this.$bvModal
        .msgBoxConfirm(`お知らせ「${title}」を削除してもよろしいですか？`, {
          title: '確認',
          okTitle: 'OK',
          okVariant: 'default',
          cancelTitle: 'キャンセル',
          hideHeaderClose: false
        })
        .then((bool) => {
          if (!bool) {
            return
          }
          api.announcement
            ._delete(announcementId)
            .then(() => {
              this.$root.$bvToast.toast(`お知らせ「${title}」を削除しました。`, {
                variant: 'success',
                toaster: 'b-toaster-top-center',
                autoHideDelay: 3000
              })
              this.update('')
              this.isDetail = false
            })
            .catch((err) => {
              const apiStatus = baseJs.getErrorStatus(err)
              this.showToast(apiStatus.errors.join('\n'), false)
            })
        })
    },
    // 一覧から、IDを使って成果のインデックスを取得
    getAnnouncementIndexById(announcementId) {
      if (!announcementId || !this.filteredAnnouncements) {
        return null
      }
      let index = -1
      this.filteredAnnouncements.find((announcement, i) => {
        if (announcement.announcement_id === announcementId) {
          index = i
          return true
        }
      })
      return index
    },
    getTitleClass(status) {
      if (baseJs.isAuth()) {
        return ''
      }
      return {
        0: 'unread',
        1: 'read',
        2: 'replyed'
      }[status]
    },

    async openEditForm(announcementId, toUserIds = []) {
      if (announcementId) {
        this.modalTitle = 'お知らせの編集'
        if (!this.isDetail) {
          await this.$store.dispatch('Announcement/getInfo', { announcementId })
        }
        this.announcementInfoForEdit = Object.assign({}, getArrangedAnnouncement(this.announcement))
        this.announcementInfoForEdit.to_user_ids = this.listReaction.map((user) => user.id)
      } else {
        // 新規作成
        this.modalTitle = this.addButtonText
        this.announcementInfoForEdit = getEmptyAnnouncementInfo()
        this.announcementInfoForEdit.to_user_ids = toUserIds
      }
      this.$nextTick().then((e) => {
        this.$bvModal.show('announcement-modal')
      })
    },

    closeEditForm(edited) {
      if (edited) {
        this.isDetail = false
      }
      this.$bvModal.hide('announcement-modal')
    },

    async handleClickDetail(announcementId) {
      this.$store.dispatch('Announcement/getInfo', { announcementId })
      this.detailIndex = this.getAnnouncementIndexById(announcementId)
      this.isDetail = true
    },
    async handleRead(announcementId) {
      await api.announcement.open(Number(announcementId))
    },

    showOther(index) {
      const announcementId = this.filteredAnnouncements[index].announcement_id
      this.handleClickDetail(announcementId)
    },
    showList() {
      this.$store.dispatch('Announcement/getAnnouncementList')
      this.isDetail = false
    },
    updateSearch(e) {
      this.search = e
      this.timer = 3
    },
    update(message = '') {
      this.$emit('update', message)
    },

    async handleClickReply({ announcementId, replyType, comment }) {
      if (!replyType || replyType === '0') {
        this.showErrorToast('返信内容を選択してください。')
        return
      }
      await this.$store
        .dispatch('Announcement/reply', {
          announcementId,
          replyType,
          comment
        })
        .then(() => this.showSuccessToast('返信しました。'))
        .catch((err) => {
          const errors = baseJs.getErrorStatus(err)
          this.showErrorToast(errors.customeMessage)
        })
      this.$store.dispatch('Announcement/getInfo', { announcementId })
    },
    async handleClickProfile(urlString) {
      this.profileUrlString = urlString
      this.$nextTick().then((e) => {
        this.$bvModal.show('profile-modal')
      })
    },
    async handleClickFavorite({ announcementId, toBeFavorite }) {
      await this.$store
        .dispatch('Announcement/favorite', {
          announcementId,
          toBeFavorite
        })
        .then(() => {
          const message = toBeFavorite === 1 ? 'いいねしました' : 'いいねを外しました'
          this.showSuccessToast(message)
        })
        .catch((err) => {
          const errors = baseJs.getErrorStatus(err)
          this.showErrorToast(errors.errors.join('\n'))
        })
      this.$store.dispatch('Announcement/getInfo', { announcementId })
    },
    showSuccessToast(message) {
      this.$root.$bvToast.toast(message, {
        variant: 'success',
        toaster: 'b-toaster-top-center',
        autoHideDelay: 3000
      })
    },
    showErrorToast(message) {
      this.$root.$bvToast.toast(message, {
        variant: 'danger',
        toaster: 'b-toaster-top-center',
        toastClass: 'pre-wrap',
        noAutoHide: true
      })
    }
  },
  created() {
    setInterval(() => {
      // 3秒間検索条件が操作されなかったら検索条件を履歴に残す
      const timer = this.timer
      if (timer === 1) {
        let functionId = 2
        if (baseJs.isAuth()) {
          functionId = 1
        }
        api.log.logSearchHistory(this.search, functionId)
      }
      if (timer > 0) {
        this.timer--
      }
    }, 1000)
  }
}
</script>

<style scoped>
.draft {
  font-size: 12px;
}
.read {
  margin-left: 22px;
}
.unread {
  font-weight: bold;
}
.unread:before {
  content: '●';
  margin-right: 0.25rem;
  margin-left: 0.25rem;
  color: #6bc3d4;
}

.edit-announcement:hover,
.delete-announcement:hover {
  cursor: pointer;
  opacity: 0.9;
}
</style>
